import {createAsyncThunk, createSlice, current} from "@reduxjs/toolkit";
// redux
import {child, get, push, update} from "firebase/database";
import {AUTH, dbRef, waitForCurrentUser} from "../../auth/FirebaseContext";
import {putOperatorsOffline} from "../../store/offlineDb";
import {OPERATORS_GROUP} from "../../constants";
import {sanitizeString} from "../../utils/other";
import {normalizeCardNumber} from "../../utils/cardNumber";
import {getSessionOperator, setSessionOperator} from "../../helper/session";
// ----------------------------------------------------------------------

export const fetchAllOperators = createAsyncThunk("fetchAllOperators", async () => {
    const user = await waitForCurrentUser();
    if (!user) {
        throw new Error("Prijava još nije potvrđena. Sačekajte trenutak i pokušajte ponovo.");
    }

    const arr = [];
    (await get(child(dbRef, `users/${user.uid}/private/operators`))).forEach(child1 => {
        if (GROUPS_IDS_OPERATORS.includes(parseInt(child1.val().group))) {
            arr.push({
                ...child1.val(),
                uid: child1.key
            });
        }
    });
    try {
        await putOperatorsOffline(arr);
    } catch (e) {
        console.error("putOperatorsOffline", e);
    }
    return arr;
});

/**
 * Bez ključa naloga upis odlazi na putanju `.../operators/undefined`, a RTDB tu putanju
 * napravi - umesto izmene postojećeg naloga dobije se nov, koji niko nije tražio.
 */
const assertKnownOperator = (operator) => {
    if (!operator?.uid) {
        throw new Error("Nalog nije prepoznat. Osvežite listu naloga pa pokušajte ponovo.");
    }
};

/**
 * Prijava uzima prvog operatera sa unetom šifrom, pa dve iste šifre znače da se uloguje
 * pogrešan čovek. Čita se sveže stanje iz baze, jer je nalog mogao dodati i drugi korisnik.
 */
const assertSecretIsFree = async (userUid, secret, ignoreUid) => {
    let takenBy = null;
    (await get(child(dbRef, `users/${userUid}/private/operators`))).forEach(child1 => {
        if (child1.key !== ignoreUid && String(child1.val()?.secret) === String(secret)) {
            takenBy = child1.val()?.username || "";
            return true;
        }
        return false;
    });

    if (takenBy !== null) {
        throw new Error(takenBy
            ? `Šifra je već dodeljena operateru: ${takenBy}`
            : "Šifra je već dodeljena drugom operateru");
    }
};

const driverCardNumber = (operator) => {
    if (Number(operator?.group) !== OPERATORS_GROUP.driver) {
        return null;
    }
    return normalizeCardNumber(operator?.cardNumber) || null;
};

const assertCardIsFree = async (userUid, cardNumber, ignoreUid) => {
    if (!cardNumber) {
        return;
    }
    let takenBy = null;
    (await get(child(dbRef, `users/${userUid}/private/operators`))).forEach(child1 => {
        const value = child1.val();
        const sameCard = normalizeCardNumber(value?.cardNumber) === cardNumber;
        const sameSecret = String(value?.secret) === cardNumber;
        if (child1.key !== ignoreUid && (sameCard || sameSecret)) {
            takenBy = value?.username || "";
            return true;
        }
        return false;
    });

    if (takenBy !== null) {
        throw new Error(takenBy
            ? `Kartica je već dodeljena operateru: ${takenBy}`
            : "Kartica je već dodeljena drugom operateru");
    }
};

export const createOperator = createAsyncThunk("createOperator", async (operator) => {
    await assertSecretIsFree(AUTH.currentUser.uid, operator.secret);
    const cardNumber = driverCardNumber(operator);
    await assertCardIsFree(AUTH.currentUser.uid, cardNumber);

    const obj = {
        username: operator.username,
        secret: String(operator.secret),
        printText: sanitizeString(operator?.printText),
        group: Number(operator.group),
        inactive: false,
        cardNumber,
        cityNoFiscal: operator.cityNoFiscal === true
    };
    const created = await push(child(dbRef, `users/${AUTH.currentUser.uid}/private/operators`),
        obj);
    return {...obj, uid: created.key};
});

export const changeOperatorStatus = createAsyncThunk("changeOperatorStatus", async (operator) => {
    assertKnownOperator(operator);

    (await update(child(dbRef, `users/${AUTH.currentUser.uid}/private/operators/${operator.uid}`), {
        ...operator,
        inactive: !operator.inactive
    }));
    return {
        ...operator,
        inactive: !operator.inactive
    };
});

export const updateOperator = createAsyncThunk("updateOperator", async (operator) => {
    assertKnownOperator(operator);
    await assertSecretIsFree(AUTH.currentUser.uid, operator.secret, operator.uid);
    const cardNumber = driverCardNumber(operator);
    await assertCardIsFree(AUTH.currentUser.uid, cardNumber, operator.uid);

    const obj = {
        cardNumber,
        cityNoFiscal: operator.cityNoFiscal === true,
        group: Number(operator.group),
        // Izmena naloga ne sme da vrati u rad nalog koji je neko namerno ugasio.
        inactive: operator.inactive === true,
        printText: sanitizeString(operator?.printText),
        secret: String(operator.secret),
        uid: operator.uid,
        username: operator.username
    };
    await update(child(dbRef, `users/${AUTH.currentUser.uid}/private/operators/${operator.uid}`), obj);
    return obj;
});

const initialState = {
    loading: true,
    error: null,
    allOperators: [],
    operator: null
};

// Reducer
export const operatorsSlice = createSlice({
    name: "operators",
    initialState,
    reducers: {
        // logInOperator: (state, {payload}) => {
        //     setSessionOperator(payload);
        //     state.operator = payload;
        // },
        // logOutOperator: (state) => {
        //     removeSessionOperator();
        //     state.operator = undefined;
        // }
    },
    extraReducers: {
        [fetchAllOperators.pending]: (state) => {
            state.loading = true;
        },
        [fetchAllOperators.fulfilled]: (state, {payload}) => {
            state.allOperators = payload.sort((a, b) => a.group - b.group);
            state.loading = false;
        },
        [fetchAllOperators.rejected]: (state) => {
            state.loading = false;
        },
        [changeOperatorStatus.pending]: (state) => {
            state.loading = true;
        },
        [changeOperatorStatus.fulfilled]: (state, {payload}) => {
            const arr = [...current(state.allOperators)];
            const index = arr.findIndex(o => o.uid === payload.uid);
            if (index !== -1) {
                arr[index] = payload;
                state.allOperators = arr;
            }
            state.loading = false;
        },
        [changeOperatorStatus.rejected]: (state) => {
            state.loading = false;
        },
        [updateOperator.pending]: (state) => {
            state.loading = true;
        },
        [updateOperator.fulfilled]: (state, {payload}) => {
            const arr = [...current(state.allOperators)];
            const index = arr.findIndex(o => o.uid === payload.uid);
            if (index !== -1) {
                arr[index] = payload;
                state.allOperators = arr;
            }
            const sessionOp = getSessionOperator();
            if (sessionOp && sessionOp.uid === payload.uid) {
                setSessionOperator(payload);
            }
            state.loading = false;
        },
        [updateOperator.rejected]: (state) => {
            state.loading = false;
        },
        [createOperator.pending]: (state) => {
            state.loading = true;
        },
        [createOperator.fulfilled]: (state, {payload}) => {
            const arr = [...current(state.allOperators)];
            arr.push(payload);
            state.allOperators = arr;
            state.loading = false;
        },
        [createOperator.rejected]: (state) => {
            state.loading = false;
        }
    }
});

export const operatorsReducer = operatorsSlice.reducer;

const GROUPS_IDS_OPERATORS = Object.values(OPERATORS_GROUP);
